import { request } from '@umijs/max';
import Settings from '../../config/defaultSettings';
import { log } from 'console';
import { getApiBase } from '@/utils/apiBase';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  status: number;
  message: string;
  data: {
    fk_user: number;
    user: string;
    email: string;
    cedula: string;
    fk_rol: number;
    rol: string;
    token: string;
  };
}

export interface LogoutResponse {
  message: string;
}

export interface ValidateTokenResponse {
  isValid: boolean;
}

export interface UserSession {
  fk_user: number;
  user: string;
  email: string;
  cedula: string;
  fk_rol: number;
  rol: string;
  token: string;
  isAuthenticated: boolean;
}

class AuthService {
  private readonly API_BASE_URL: string;
  private readonly AUTH_TOKEN: string;
  private readonly TOKEN_KEY = 'token';
  private readonly SESSION_KEY = 'session';

  constructor() {
    // Debug environment variables
    // console.log('Environment variables check:', {
    //   UMI_APP_API_URL: process.env.UMI_APP_API_URL,
    //   UMI_APP_API_TOKEN: process.env.UMI_APP_API_TOKEN,
    //   NODE_ENV: process.env.NODE_ENV,
    //   REACT_APP_ENV: process.env.REACT_APP_ENV
    // });

    // Try multiple approaches to get environment variables
    this.API_BASE_URL = getApiBase();
      
    this.AUTH_TOKEN = 
      process.env.UMI_APP_API_TOKEN || 
      (window as any).__UMI_ENV__?.UMI_APP_API_TOKEN ||
      '';

    console.log('AuthService initialized with:', {
      API_BASE_URL: this.API_BASE_URL,
      AUTH_TOKEN: this.AUTH_TOKEN ? '***TOKEN_SET***' : 'NO_TOKEN'
    });
  }

  /**
   * Login user with email and password
   * @param credentials - Email and password for authentication
   * @returns Promise with login response
   */ async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {

      const response = await request<LoginResponse>(`${this.API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.AUTH_TOKEN}`,
        },
        data: {
          email: credentials.email,
          password: credentials.password,
        },
        skipErrorHandler: true, // Handle errors manually for better UX
      });      if (response?.status === 200 && response?.data?.token) {
        // Store token and session information
        this.setToken(response.data.token);
        this.setSession(response.data);
        
        // Start session monitoring after successful login
        const { default: sessionMonitor } = await import('./SessionMonitor');
        sessionMonitor.startMonitoring();

        // Reload page after successful login for all users
        // Use setTimeout to ensure the response is returned before reload
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }

      return response;
    } catch (error: any) {
      

      // Re-throw with enhanced error information
      if (error?.response?.data) {
        throw {
          ...error,
          message: error.response.data.message || 'Error al iniciar sesión',
          status: error.response.status || 500,
        };
      }

      throw error;
    }
  }
  /**
   * Logout current user and invalidate session
   * @returns Promise with logout response
   */
  async logout(): Promise<LogoutResponse> {
    try {
      // Stop session monitoring
      const { default: sessionMonitor } = await import('./SessionMonitor');
      sessionMonitor.stopMonitoring();

      const token = this.getToken();

      if (token) {
        const response = await request<LogoutResponse>(`${this.API_BASE_URL}/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          skipErrorHandler: true, // Don't show error messages for logout
        });

        // Clear local storage regardless of response
        this.clearAuthData();

        return response;
      } else {
        // If no token, just clear local data
        this.clearAuthData();
        return { message: 'Session cleared successfully' };
      }
    } catch (error) {
      // Clear local data even if logout request fails
      this.clearAuthData();
      throw error;
    }
  }  async validateToken(): Promise<boolean> {
    try {
      const token = this.getToken();

      if (!token) {
        return false;
      }

      const response = await request<boolean>(`${this.API_BASE_URL}/validate-token`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        skipErrorHandler: true,
      });


      const isValid = response;

      if (!isValid) {
        this.clearAuthData();
        // Stop monitoring if token is invalid
        const { default: sessionMonitor } = await import('./SessionMonitor');
        sessionMonitor.stopMonitoring();
      }

      return isValid;
    } catch (error: any) {
      if (error?.response?.status === 498 || error?.response?.status === 401) {
        this.clearAuthData();
        const { default: sessionMonitor } = await import('./SessionMonitor');
        sessionMonitor.stopMonitoring();
      }

      return false;
    }
  }

  /**
   * Check if user is currently authenticated
   * This checks both local token existence and validates with server
   * @returns Promise<boolean> indicating authentication status
   */
  async isAuthenticated(): Promise<boolean> {
    const token = this.getToken();

    if (!token) {
      return false;
    }

    // Validate token with server
    return await this.validateToken();
  }

  /**
   * Get current user session information
   * @returns UserSession object or null if not authenticated
   */
  getCurrentSession(): UserSession | null {
    try {
      const sessionData = localStorage.getItem(this.SESSION_KEY);
      const token = this.getToken();

      if (!sessionData || !token) {
        return null;
      }

      const session = JSON.parse(sessionData);

      return {
        ...session,
        isAuthenticated: true,
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Get current authentication token
   * @returns Token string or null if not found
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Set authentication token in local storage
   * @param token - JWT token to store
   */
  private setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  /**
   * Set user session data in local storage
   * @param sessionData - User session information
   */
  private setSession(sessionData: LoginResponse['data']): void {
    localStorage.setItem(this.SESSION_KEY, JSON.stringify(sessionData));
  }

  /**
   * Clear all authentication data from local storage
   */
  private clearAuthData(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.SESSION_KEY);
    // Clear user reload flag on logout
    sessionStorage.removeItem('user_page_reloaded');
  }

  /**
   * Get authorization header for API requests
   * @returns Authorization header object or empty object
   */
  getAuthHeader(): { Authorization?: string } {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Check if user has specific role
   * @param roleName - Role name to check
   * @returns boolean indicating if user has the role
   */
  hasRole(roleName: string): boolean {
    const session = this.getCurrentSession();
    return session?.rol === roleName;
  }

  /**
   * Check if user has specific role ID
   * @param roleId - Role ID to check
   * @returns boolean indicating if user has the role ID
   */
  hasRoleId(roleId: number): boolean {
    const session = this.getCurrentSession();
    return session?.fk_rol === roleId;
  }

  /**
   * Get current user information
   * @returns User information or null
   */
  getCurrentUser(): { id: number; name: string; email: string; role: string } | null {
    const session = this.getCurrentSession();

    if (!session) {
      return null;
    }

    return {
      id: session.fk_user,
      name: session.user,
      email: session.email,
      role: session.rol,
    };
  }

  /**
   * Refresh token validation (useful for periodic checks)
   * @returns Promise<boolean> indicating if token is still valid
   */
  async refreshTokenValidation(): Promise<boolean> {
    return await this.validateToken();
  }
  /**
   * Auto-logout if token is invalid (for use in interceptors)
   */
  async autoLogoutIfInvalid(): Promise<void> {
    const isValid = await this.validateToken();
    if (!isValid) {
      await this.logout();
      // Redirect to login page
      window.location.href = '/#/login';
    }
  }

  /**
   * Initialize authentication state by checking existing token
   * @returns Promise<boolean> indicating if user is authenticated
   */
  async initialize(): Promise<boolean> {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    try {
      const isValid = await this.validateToken();
      if (isValid) {
        // Start monitoring if user is already authenticated
        const { default: sessionMonitor } = await import('./SessionMonitor');
        sessionMonitor.startMonitoring();
      }
      return isValid;
    } catch (error) {
      this.clearAuthData();
      return false;
    }
  }

  /**
   * Check if the user session is expired
   * @returns boolean indicating if session is expired
   */
  isSessionExpired(): boolean {
    const session = this.getCurrentSession();
    if (!session) {
      return true;
    }

    const token = this.getToken();
    if (!token) {
      return true;
    }

    // Additional client-side validation can be added here
    return false;
  }

  /**
   * Get user permissions from backend
   * @returns Promise with user permissions data
   */
  async getUserPermissions(): Promise<any> {
    try {
      // Get session to extract role ID
      const session = this.getCurrentSession();
      if (!session || !session.fk_rol) {
      throw new Error('No session or role ID found');
      }
      // Use the dedicated endpoint for permissions by role
      const response = await request(`${this.API_BASE_URL}/permission-rol/${session.fk_rol}`, {
      method: 'GET',
      headers: this.getAuthHeader(),
      });
      
      return response;
    } catch (error) {
      
      // Fallback: try to get permissions by role ID
      const session = this.getCurrentSession();
      if (session && session.fk_rol) {
        try {
          const rolePermissions = await this.getPermissionsByRole(session.fk_rol);
          return rolePermissions;
        } catch (fallbackError) {
        }
      } else {
        console.warn('️ [AuthService] No session or role ID available for fallback');
      }
      
      throw error;
    }
  }

  /**
   * Get permissions by role ID
   * @param roleId - The role ID to get permissions for
   * @returns Promise with permissions data filtered by role
   */
  async getPermissionsByRole(roleId: number): Promise<any> {
    try {
      // Use the new dedicated endpoint for role permissions
      const response = await request(`${this.API_BASE_URL}/permission-rol/${roleId}`, {
        method: 'GET',
        headers: this.getAuthHeader(),
      });
      
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user permissions based on role (legacy method - keeping for compatibility)
   * @returns Array of permissions or empty array
   */
  getUserPermissionsLegacy(): string[] {
    const session = this.getCurrentSession();
    if (!session) {
      return [];
    }

    // This could be extended to fetch permissions from an API
    // For now, return basic permissions based on role
    switch (session.rol.toLowerCase()) {
      case 'admin':
        return ['read', 'write', 'delete', 'admin'];
      case 'manager':
        return ['read', 'write'];
      case 'user':
        return ['read'];
      default:
        return [];
    }
  }

  /**
   * Check if user has specific permission
   * @param permission - Permission to check
   * @returns boolean indicating if user has the permission
   */
  hasPermission(permission: string): boolean {
    const permissions = this.getUserPermissionsLegacy();
    return permissions.includes(permission.toLowerCase());
  }
  

  /**
   * Valida el token de sesión contra el backend usando GET y Authorization header.
   * @returns Promise<boolean> true si la sesión es válida, false si no.
   */
  async isSessionValid(): Promise<boolean> {
    const token = this.getToken();
    if (!token) return false;
    try {
      const response = await request<boolean>(`${this.API_BASE_URL}/validate-token`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        skipErrorHandler: true,
      });
      // El backend retorna true si es válido, false si no.
      if (response === true) return true;
      // Si no es válido, limpiar datos locales.
      this.clearAuthData();
      return false;
    } catch (error: any) {
      // Si el backend responde 401, limpiar datos locales.
      if (error?.response?.status === 401) {
        this.clearAuthData();
      }
      return false;
    }
  }

  /**
   * Verifica la sesión y redirige si está expirada.
   * @returns Promise<void>
   */
  async checkSessionOrRedirect(): Promise<void> {
    const valid = await this.isSessionValid();
    if (!valid) {
      this.clearAuthData();
      window.location.href = '/#/login';
    }
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;
